import {
  resolveFaceShapeByRef,
  resolveFaceShapesByEdge,
} from '../../common';
import { resolveEdgeSupportFace } from '../../edge-support-face-resolution';
import type { ChamferKernelInstance } from '../types/ChamferShapeBuildInput';

type Vector3Tuple = [number, number, number];

type DisposablePuppyObject = {
  delete?: () => void;
};

/**
 * 等距面倒角支撑面解析输入。
 */
export interface OffsetFaceSupportFaceInput {
  /** Puppy CAD 内核 实例。 */
  puppy: ChamferKernelInstance;
  /** 当前注册边所在的目标 Shape，部分边线场景下是分割后的 Solid。 */
  targetShape: unknown;
  /** 当前目标 Shape 的元数据。 */
  targetMetadata: Record<string, unknown>;
  /** 原始未分割源 Shape，用于解析用户选择时携带的相邻面引用。 */
  sourceShape: unknown;
  /** 原始未分割源 Shape 的元数据。 */
  sourceMetadata: Record<string, unknown>;
  /** 用户选择项。 */
  selection: SelectionItemRef;
  /** 当前目标边 Shape。 */
  edgeShape: unknown;
  /** 是否正在处理部分边线分割拓扑。 */
  useSplitTopologySupport: boolean;
  /** SolidWorks 方向翻转对应的相邻面偏移。 */
  preferredFaceOffset: 0 | 1;
}

/**
 * 释放 Puppy CAD 内核 临时对象。
 *
 * @param value Puppy CAD 内核 对象。
 */
function safeDelete(value: unknown): void {
  const deleteMethod = (value as DisposablePuppyObject | null)?.delete;
  if (typeof deleteMethod !== 'function') {
    return;
  }
  try {
    deleteMethod.call(value);
  } catch {
    // 临时 Puppy CAD 内核 对象释放失败不应覆盖真正的倒角求解错误。
  }
}

/**
 * 计算面的单位法向。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param face 目标面。
 * @returns 单位法向；不可计算时返回 `null`。
 */
function computeFaceNormal(puppy: ChamferKernelInstance, face: unknown): Vector3Tuple | null {
  const surface = new puppy.BRepAdaptor_Surface_2(face as never, true);
  const point = new puppy.gp_Pnt_3(0, 0, 0);
  const d1u = new puppy.gp_Vec_4(0, 0, 0);
  const d1v = new puppy.gp_Vec_4(0, 0, 0);
  let normalVec: unknown = null;
  try {
    const uParam = (surface.FirstUParameter() + surface.LastUParameter()) / 2;
    const vParam = (surface.FirstVParameter() + surface.LastVParameter()) / 2;
    surface.D1(uParam, vParam, point, d1u, d1v);
    normalVec = d1u.Crossed(d1v);
    const magnitude = (normalVec as { Magnitude: () => number }).Magnitude();
    if (!Number.isFinite(magnitude) || magnitude <= 1e-8) {
      return null;
    }
    return [
      (normalVec as { X: () => number }).X() / magnitude,
      (normalVec as { Y: () => number }).Y() / magnitude,
      (normalVec as { Z: () => number }).Z() / magnitude,
    ];
  } finally {
    safeDelete(normalVec);
    safeDelete(d1v);
    safeDelete(d1u);
    safeDelete(point);
    safeDelete(surface);
  }
}

/**
 * 计算两个法向的绝对点积。
 *
 * @param left 左法向。
 * @param right 右法向。
 * @returns 绝对点积。
 */
function resolveNormalSimilarity(left: Vector3Tuple, right: Vector3Tuple): number {
  return Math.abs(
    left[0] * right[0]
    + left[1] * right[1]
    + left[2] * right[2],
  );
}

/**
 * 解析用户选择边在原始源体上的首选支撑面。
 *
 * @param input 支撑面解析输入。
 * @returns 源支撑面；无法解析时返回 `null`。
 */
function resolvePreferredSourceFace(input: OffsetFaceSupportFaceInput): unknown | null {
  const preferredFaceRefs = (input.selection.adjacentFaceRefs ?? [])
    .filter((candidate): candidate is string => typeof candidate === 'string' && candidate.length > 0);
  const preferredRef = preferredFaceRefs[input.preferredFaceOffset] ?? preferredFaceRefs[0] ?? null;
  if (!preferredRef) {
    return null;
  }
  try {
    return resolveFaceShapeByRef(input.puppy, input.sourceShape, input.sourceMetadata, preferredRef);
  } catch {
    return null;
  }
}

/**
 * 在分割后的目标 Solid 上匹配与源支撑面方向一致的相邻面。
 *
 * @param input 支撑面解析输入。
 * @param sourceSupportFace 原始源支撑面。
 * @returns 分割目标 Solid 上的对应支撑面；无法稳定匹配时返回 `null`。
 */
function resolveSplitSupportFaceBySourceFace(
  input: OffsetFaceSupportFaceInput,
  sourceSupportFace: unknown,
): unknown | null {
  const sourceNormal = computeFaceNormal(input.puppy, sourceSupportFace);
  if (!sourceNormal) {
    return null;
  }
  const adjacentFaces = resolveFaceShapesByEdge(input.puppy, input.targetShape, input.edgeShape);
  const bestMatch = adjacentFaces.reduce<{
    face: unknown | null;
    score: number;
  }>((state, candidateFace) => {
    const candidateNormal = computeFaceNormal(input.puppy, candidateFace);
    if (!candidateNormal) {
      return state;
    }
    const score = resolveNormalSimilarity(sourceNormal, candidateNormal);
    return score > state.score ? { face: candidateFace, score } : state;
  }, { face: null, score: Number.NEGATIVE_INFINITY });

  /**
   * 立方体等常见拓扑的相邻支撑面互相垂直，0.95 足以区分“同一来源面”和另一侧面。
   * 低于该阈值时说明 split 后的相邻面已经无法通过法向稳定归属，交给通用回退路径报错或兜底。
   */
  return bestMatch.face && bestMatch.score >= 0.95 ? bestMatch.face : null;
}

/**
 * 解析等距面倒角支撑面。
 *
 * 设计说明：
 * 1. 常规整边倒角直接使用通用 `resolveEdgeSupportFace`；
 * 2. 部分边线会先切分 Solid，split 后相邻面枚举顺序可能变化；
 * 3. 因此 split 场景先按用户选择时携带的源相邻面解析原始支撑面，再在分割 Solid 上找法向一致的对应面，
 *    保证非对称距离不会因为 split 枚举顺序变化而自动反向。
 *
 * @param input 支撑面解析输入。
 * @returns Puppy CAD 内核 支撑面 Shape。
 */
export function resolveOffsetFaceSupportFace(input: OffsetFaceSupportFaceInput): unknown {
  if (!input.useSplitTopologySupport) {
    return resolveEdgeSupportFace(
      input.puppy,
      input.targetShape,
      input.targetMetadata,
      input.selection,
      input.edgeShape,
      { preferredFaceOffset: input.preferredFaceOffset },
    );
  }

  const sourceSupportFace = resolvePreferredSourceFace(input);
  const splitSupportFace = sourceSupportFace
    ? resolveSplitSupportFaceBySourceFace(input, sourceSupportFace)
    : null;
  if (splitSupportFace) {
    return splitSupportFace;
  }

  /**
   * 回退时清空相邻面引用，避免把源体 face ref 当作 split 后的 face index 使用。
   * 这条路径只用于缺少相邻面提示或法向不可计算的边缘情况。
   */
  return resolveEdgeSupportFace(
    input.puppy,
    input.targetShape,
    input.targetMetadata,
    { ...input.selection, adjacentFaceRefs: [] },
    input.edgeShape,
    { preferredFaceOffset: input.preferredFaceOffset },
  );
}
